import React, { useCallback, useRef, useState } from 'react';
import { api } from '../lib/api';

export interface CloseProof {
  reason: string;
  imageUrl?: string;
}

interface Props {
  isOpen: boolean;
  issueTitle: string;
  onConfirm: (proof: CloseProof) => Promise<void>;
  onCancel: () => void;
}

export default function CloseIssueModal({ isOpen, issueTitle, onConfirm, onCancel }: Props) {
  const [reason,       setReason]       = useState('');
  const [imageFile,    setImageFile]    = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragging,     setDragging]     = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [error,        setError]        = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) addFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) addFile(file);
    e.target.value = '';
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleConfirm = async () => {
    if (!reason.trim() && !imageFile) {
      setError('Please add a resolution note or attach a screenshot — at least one is required.');
      return;
    }
    setError('');
    setUploading(true);

    let imageUrl: string | undefined;
    if (imageFile) {
      try {
        const form = new FormData();
        form.append('file', imageFile);
        const { url } = await api.upload<{ url: string }>('/api/upload', form);
        imageUrl = url;
      } catch { /* upload failed, proceed without image */ }
    }

    await onConfirm({ reason: reason.trim(), imageUrl });
    setUploading(false);
    // Reset for next use
    setReason('');
    setImageFile(null);
    setImagePreview(null);
  };

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !uploading) onCancel();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div className="modal-panel close-issue-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-icon close-issue-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="modal-title">Close Issue</p>
            <p className="modal-subtitle" title={issueTitle}>{issueTitle}</p>
          </div>
          {!uploading && (
            <button className="modal-close-btn" onClick={onCancel} aria-label="Cancel">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="modal-body">
          <p className="close-issue-instructions">
            Document how this issue was resolved before closing. Provide a text explanation,
            a screenshot, or both — <strong>at least one is required</strong>.
          </p>

          {/* Text reason */}
          <div className="form-group">
            <label className="form-label">
              Resolution notes
              <span className="form-label-hint">(what was fixed and how)</span>
            </label>
            <textarea
              className="form-textarea close-proof-textarea"
              placeholder="Describe how this issue was resolved…&#10;e.g. Fixed the null pointer in auth middleware by adding a guard check."
              value={reason}
              onChange={e => { setReason(e.target.value); setError(''); }}
              rows={5}
              disabled={uploading}
              autoFocus
            />
          </div>

          {/* Image proof */}
          <div className="form-group">
            <label className="form-label">
              Screenshot proof
              <span className="form-label-hint">(optional when notes are provided)</span>
            </label>

            {imagePreview ? (
              <div className="close-proof-preview-wrap">
                <img src={imagePreview} alt="Proof screenshot" className="close-proof-preview-img" />
                <button
                  type="button"
                  className="close-proof-remove-btn"
                  onClick={removeImage}
                  title="Remove screenshot"
                  disabled={uploading}
                >×</button>
                <span className="close-proof-preview-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Screenshot attached
                </span>
              </div>
            ) : (
              <div
                className={`drop-zone close-proof-dropzone${dragging ? ' dragging' : ''}`}
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" className="upload-icon" width="32" height="32">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <p className="drop-zone-text" style={{ fontSize: 13 }}>
                  Click to browse or drag &amp; drop a screenshot
                </p>
                <p className="drop-zone-hint">PNG, JPG, WEBP — Ctrl+V paste also works</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="file-input-hidden"
                />
              </div>
            )}
          </div>

          {error && (
            <div className="close-proof-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel} disabled={uploading}>
            Cancel
          </button>
          <button className="btn btn-close-issue" onClick={handleConfirm} disabled={uploading}>
            {uploading ? (
              <><span className="btn-spinner" /> Closing…</>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Close Issue
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
